import {
  type AgendaItem,
  type CaptureItem,
  type CaptureItemInput,
  type EntityId,
  type FeatureServices,
  type Reminder,
  type ReminderInput,
  type ReminderState,
  type Review,
  type ReviewInput,
  type ReviewOutcome,
  type ReviewPrompt,
  type Task,
  type TaskInput,
  type TaskLane,
  type TodayPlan,
  type Target,
  type TargetInput,
  type TargetStatus,
  type ConsistencyEvidence,
  type ConsistencySummary,
  type Habit,
  type HabitCheckin,
  type HabitInput,
  type HabitStatus,
  type HabitSummary,
  type Routine,
  type RoutineInput,
  type Problem,
  type ProblemInput,
  type ProblemPatch,
  type ProblemStatus,
  type Project,
  type ProjectInput,
  type ProjectPatch,
  type ProjectStatus,
  type CalendarEvent,
  type EventInput,
  type EventOccurrence,
  type EventPatch,
  type EventStatus,
  type Account,
  type AccountInput,
  type Transaction,
  type TransactionInput,
  type ImportBatch,
  type ImportRow,
  type FinanceSummary,
  type Note,
  type NoteInput,
  type NotePatch,
  type KnowledgeGraph,
  type Goal,
  type GoalInput,
  type GoalPatch,
  type GoalProgress,
  type GoalStatus,
  type Milestone,
  type MilestoneInput,
  type MilestoneStatus,
  type DirectionItem,
  type DirectionItemInput,
  type DirectionLens,
  type ControlCategory,
  type HealthLog,
  type HealthLogInput,
  type HealthSummary,
  type Brief,
  type StudySubject,
  type StudySubjectInput,
  type StudySession,
  type StudySessionInput,
  type StudySummary,
  type MasteryState,
  type SubjectStatus,
  type Opportunity,
  type OpportunityInput,
  type OpportunityPatch,
  type OpportunityStage,
  type JournalEntry,
  type JournalEntryInput,
  type JournalStream,
  type JournalSummary,
  type ContentItem,
  type ContentItemInput,
  type ContentItemPatch,
  type ContentStage,
  type Venture,
  type VentureInput,
  type VenturePatch,
  type VentureStatus,
  type InventoryItem,
  type InventoryItemInput,
  type InventoryItemPatch,
  type ItemStatus,
  type FocusSession,
  type FocusSessionInput,
  type FocusSummary,
  type Profile,
  type ProfileInput,
  type Admission,
  type AdmissionInput,
  type AdmissionStatus,
  type OfferStatus,
  type FundingSource,
  type FundingSourceInput,
  type FundingStatus,
  type TouchpointChannel,
  type EducationProgram,
  type EducationProgramInput,
  type ResumeProfile,
  type ResumeProfileInput,
  type LockStatus,
  type SecurityConfig,
  type SecurityEvent,
  DEFAULT_SECURITY_CONFIG,
  lockStatus,
  MAX_REVIEW_PROMPTS,
  type RecordEnvelope,
  computeConsistency,
  computeHabitStreak,
  maskForDayType,
  expandOccurrences,
  dayWindow,
  accountBalanceMinor,
  netWorthMinor,
  summarizeTransactions,
  buildImportFingerprint,
  inferTransactionCategory,
  buildKnowledgeGraph,
  goalProgress,
  defaultItemKind,
  clampEnergy,
  summarizeHealth,
  composeBrief,
  isProjectStalled,
  isProblemOpen,
  summarizeStudy,
  defaultJournalPrivacy,
  summarizeJournal,
  summarizeFocus,
} from '@life-os/app-core';
import type { Clock } from './clock.js';
import type { MutationOp, MutationQueue } from './mutation.js';
import type {
  CaptureRepository,
  ReminderRepository,
  ReviewRepository,
  RoutineRepository,
  ProblemRepository,
  ProjectRepository,
  EventRepository,
  FinanceRepository,
  NoteRepository,
  DirectionRepository,
  HealthRepository,
  StudyRepository,
  CareerRepository,
  JournalRepository,
  ContentRepository,
  BusinessRepository,
  EnvironmentRepository,
  FocusRepository,
  SecurityRepository,
  SourceRepository,
  TargetRepository,
  TaskRepository,
  AcademicsRepository,
} from './repositories.js';
import { RecordNotFoundError } from './repositories.js';
import { createVerifier, verifyPassphrase } from './crypto.js';

export interface OwnerContext {
  readonly ownerId: string;
}

export interface ServiceDeps {
  readonly owner: OwnerContext;
  readonly queue: MutationQueue;
  readonly clock: Clock;
  readonly source: SourceRepository;
  readonly capture: CaptureRepository;
  readonly tasks: TaskRepository;
  readonly reviews: ReviewRepository;
  readonly reminders: ReminderRepository;
  readonly targets: TargetRepository;
  readonly routines: RoutineRepository;
  readonly problems: ProblemRepository;
  readonly projects: ProjectRepository;
  readonly events: EventRepository;
  readonly finance: FinanceRepository;
  readonly notes: NoteRepository;
  readonly direction: DirectionRepository;
  readonly health: HealthRepository;
  readonly study: StudyRepository;
  readonly career: CareerRepository;
  readonly journal: JournalRepository;
  readonly content: ContentRepository;
  readonly business: BusinessRepository;
  readonly environment: EnvironmentRepository;
  readonly focus: FocusRepository;
  readonly security: SecurityRepository;
  readonly academics: AcademicsRepository;
}

export class CaptureBlankError extends Error {
  constructor() {
    super('Capture text must not be blank');
    this.name = 'CaptureBlankError';
  }
}

function deriveTitle(rawText: string): string {
  const firstLine = rawText.trim().split('\n', 1)[0] ?? rawText.trim();
  return firstLine.length > 120 ? `${firstLine.slice(0, 117)}...` : firstLine;
}

/** Concrete FeatureServices over repositories + the mutation queue. */
export class DataFeatureServices implements FeatureServices {
  /** In-memory unlocked flag for the current session (never persisted). */
  private unlocked = false;

  constructor(private readonly d: ServiceDeps) {}

  private async enqueue(record: RecordEnvelope, op: MutationOp): Promise<void> {
    await this.d.queue.enqueue({
      kind: record.kind,
      entityId: record.id,
      op,
      privacyClass: record.privacyClass,
      baseVersion: record.baseVersion,
    });
  }

  readonly capture = {
    captureItem: async (input: CaptureItemInput): Promise<CaptureItem> => {
      if (input.rawText.trim().length === 0) throw new CaptureBlankError();
      const privacyClass = input.privacyClass ?? 'sensitive_private';
      // Preserve source evidence BEFORE any structured record exists.
      const source = await this.d.source.preserve({
        ownerId: this.d.owner.ownerId,
        privacyClass,
        origin: { kind: 'manual_capture' },
        rawBody: input.rawText,
      });
      const item = await this.d.capture.create({
        ownerId: this.d.owner.ownerId,
        rawText: input.rawText,
        captureType: input.captureType ?? 'unspecified',
        privacyClass,
        sourceRecordId: source.id,
      });
      await this.enqueue(item, 'create');
      return item;
    },

    listUnreviewed: (): Promise<readonly CaptureItem[]> => this.d.capture.listUnreviewed(),

    reviewCapture: async (id: EntityId, decision: ReviewOutcome): Promise<CaptureItem> => {
      const capture = await this.d.capture.get(id);
      if (!capture) throw new RecordNotFoundError('CaptureItem', String(id));

      let outcome = decision;
      if (decision.kind === 'promote_to_task') {
        const task = await this.d.tasks.create({
          ownerId: this.d.owner.ownerId,
          title: deriveTitle(capture.rawText),
          lane: 'next',
          scheduledFor: null,
          privacyClass: capture.privacyClass,
          links: { fromSourceRecordId: capture.sourceRecordId, relatedIds: [] },
        });
        await this.enqueue(task, 'create');
        if (capture.sourceRecordId) {
          await this.d.source.appendPromotion(capture.sourceRecordId, task.id);
        }
        outcome = { kind: 'promote_to_task', taskId: task.id };
      }

      const reviewed = await this.d.capture.markReviewed(id, outcome);
      await this.enqueue(reviewed, 'update');
      return reviewed;
    },
  };

  readonly tasks = {
    create: async (input: TaskInput): Promise<Task> => {
      const lane: TaskLane = input.lane ?? 'next';
      const task = await this.d.tasks.create({
        ownerId: this.d.owner.ownerId,
        title: input.title,
        lane,
        scheduledFor: input.scheduledFor ?? null,
        privacyClass: input.privacyClass ?? 'sensitive_private',
        links: {
          fromSourceRecordId: input.links?.fromSourceRecordId ?? null,
          relatedIds: input.links?.relatedIds ?? [],
        },
      });
      await this.enqueue(task, 'create');
      return task;
    },
    listByLane: (lane: TaskLane): Promise<readonly Task[]> => this.d.tasks.listByLane(lane),
    move: async (id: EntityId, lane: TaskLane, scheduledFor?: string | null): Promise<Task> => {
      const task = await this.d.tasks.move(id, lane, scheduledFor ?? null);
      await this.enqueue(task, 'update');
      return task;
    },
    complete: async (id: EntityId): Promise<Task> => {
      const task = await this.d.tasks.complete(id);
      await this.enqueue(task, 'update');
      return task;
    },
  };

  readonly reviews = {
    record: async (input: ReviewInput): Promise<Review> => {
      const review = await this.d.reviews.create({
        ownerId: this.d.owner.ownerId,
        reviewType: input.reviewType,
        forDate: input.forDate,
        notes: input.notes ?? '',
        touchedIds: input.touchedIds ?? [],
      });
      await this.enqueue(review, 'create');
      return review;
    },
    listForDate: (date: string): Promise<readonly Review[]> => this.d.reviews.listForDate(date),
    listRecent: (limit: number): Promise<readonly Review[]> => this.d.reviews.listRecent(limit),
  };

  readonly reminders = {
    schedule: async (reminder: ReminderInput): Promise<Reminder> => {
      const created = await this.d.reminders.create({
        ownerId: this.d.owner.ownerId,
        label: reminder.label,
        linkedId: reminder.linkedId ?? null,
        fireAt: reminder.fireAt,
        privacyClass: 'sensitive_private',
      });
      await this.enqueue(created, 'create');
      return created;
    },
    listFiringOn: (date: string): Promise<readonly Reminder[]> => this.d.reminders.listFiringOn(date),
    listAll: (): Promise<readonly Reminder[]> => this.d.reminders.listAll(),
    setState: async (id: EntityId, state: ReminderState): Promise<Reminder> => {
      const updated = await this.d.reminders.setState(id, state);
      await this.enqueue(updated, 'update');
      return updated;
    },
  };

  readonly targets = {
    create: async (input: TargetInput): Promise<Target> => {
      const target = await this.d.targets.createTarget({
        ownerId: this.d.owner.ownerId,
        title: input.title,
        targetType: input.targetType ?? 'output_target',
        cadence: input.cadence ?? 'daily',
        quantity: input.quantity ?? 1,
        qualityBar: input.qualityBar ?? 'Counts only if it genuinely moved the work.',
        evidenceRule: input.evidenceRule ?? 'One real, honest unit of progress.',
        minimumViableAction: input.minimumViableAction ?? 'Do the smallest honest version.',
        privacyClass: input.privacyClass ?? 'sensitive_private',
      });
      await this.enqueue(target, 'create');
      return target;
    },
    list: (): Promise<readonly Target[]> => this.d.targets.listTargets(),
    logEvidence: async (targetId: EntityId, note = ''): Promise<ConsistencyEvidence> => {
      const target = await this.d.targets.getTarget(targetId);
      if (!target) throw new RecordNotFoundError('Target', String(targetId));
      const evidence = await this.d.targets.addEvidence({
        ownerId: this.d.owner.ownerId,
        targetId,
        occurredOn: this.d.clock.nowIso(),
        note,
        privacyClass: target.privacyClass,
      });
      await this.enqueue(evidence, 'create');
      return evidence;
    },
    consistency: async (targetId: EntityId): Promise<ConsistencySummary> => {
      const target = await this.d.targets.getTarget(targetId);
      if (!target) throw new RecordNotFoundError('Target', String(targetId));
      const evidence = await this.d.targets.listEvidence(targetId);
      return computeConsistency(target, evidence, this.d.clock.nowIso());
    },
    setStatus: async (targetId: EntityId, status: TargetStatus): Promise<Target> => {
      const target = await this.d.targets.setStatus(targetId, status);
      await this.enqueue(target, 'update');
      return target;
    },
  };

  readonly routines = {
    createHabit: async (input: HabitInput): Promise<Habit> => {
      const habit = await this.d.routines.createHabit({
        ownerId: this.d.owner.ownerId,
        title: input.title,
        weekdayMask: input.weekdayMask ?? [true, true, true, true, true, true, true],
        privacyClass: input.privacyClass ?? 'sensitive_private',
      });
      await this.enqueue(habit, 'create');
      return habit;
    },
    listHabits: (): Promise<readonly Habit[]> => this.d.routines.listHabits(),
    checkIn: async (habitId: EntityId, note = ''): Promise<HabitCheckin> => {
      const habit = await this.d.routines.getHabit(habitId);
      if (!habit) throw new RecordNotFoundError('Habit', String(habitId));
      const checkin = await this.d.routines.addCheckin({
        ownerId: this.d.owner.ownerId,
        habitId,
        occurredOn: this.d.clock.nowIso(),
        note,
        privacyClass: habit.privacyClass,
      });
      await this.enqueue(checkin, 'create');
      return checkin;
    },
    habitSummary: async (habitId: EntityId): Promise<HabitSummary> => {
      const habit = await this.d.routines.getHabit(habitId);
      if (!habit) throw new RecordNotFoundError('Habit', String(habitId));
      const checkins = await this.d.routines.listCheckins(habitId);
      return computeHabitStreak(habit, checkins, this.d.clock.nowIso());
    },
    setHabitStatus: async (habitId: EntityId, status: HabitStatus): Promise<Habit> => {
      const habit = await this.d.routines.setHabitStatus(habitId, status);
      await this.enqueue(habit, 'update');
      return habit;
    },
    createRoutine: async (input: RoutineInput): Promise<Routine> => {
      const dayType = input.dayType ?? 'everyday';
      const steps = (input.steps ?? []).map((s, i) => ({
        id: `step-${i + 1}`,
        label: s.label,
        minutes: s.minutes ?? null,
      }));
      const routine = await this.d.routines.createRoutine({
        ownerId: this.d.owner.ownerId,
        title: input.title,
        dayType,
        weekdayMask: input.weekdayMask ?? maskForDayType(dayType),
        steps,
        privacyClass: input.privacyClass ?? 'sensitive_private',
      });
      await this.enqueue(routine, 'create');
      return routine;
    },
    listRoutines: (): Promise<readonly Routine[]> => this.d.routines.listRoutines(),
    markRoutineRan: async (routineId: EntityId): Promise<Routine> => {
      const routine = await this.d.routines.markRan(routineId, this.d.clock.nowIso());
      await this.enqueue(routine, 'update');
      return routine;
    },
    setRoutineStatus: async (routineId: EntityId, status: HabitStatus): Promise<Routine> => {
      const routine = await this.d.routines.setRoutineStatus(routineId, status);
      await this.enqueue(routine, 'update');
      return routine;
    },
  };

  readonly problems = {
    create: async (input: ProblemInput): Promise<Problem> => {
      const problem = await this.d.problems.createProblem({
        ownerId: this.d.owner.ownerId,
        title: input.title,
        controlCategory: input.controlCategory ?? 'control',
        limiterType: input.limiterType ?? 'fixable',
        cause: input.cause ?? '',
        intervention: input.intervention ?? '',
        nextStep: input.nextStep ?? '',
        privacyClass: input.privacyClass ?? 'sensitive_private',
      });
      await this.enqueue(problem, 'create');
      return problem;
    },
    list: (): Promise<readonly Problem[]> => this.d.problems.listProblems(),
    update: async (id: EntityId, patch: ProblemPatch): Promise<Problem> => {
      const problem = await this.d.problems.updateProblem(id, patch);
      await this.enqueue(problem, 'update');
      return problem;
    },
    setStatus: async (id: EntityId, status: ProblemStatus): Promise<Problem> => {
      const problem = await this.d.problems.setStatus(id, status);
      await this.enqueue(problem, 'update');
      return problem;
    },
  };

  readonly projects = {
    create: async (input: ProjectInput): Promise<Project> => {
      const project = await this.d.projects.createProject({
        ownerId: this.d.owner.ownerId,
        title: input.title,
        summary: input.summary ?? '',
        nextAction: input.nextAction ?? '',
        definitionOfDone: input.definitionOfDone ?? '',
        privacyClass: input.privacyClass ?? 'sensitive_private',
      });
      await this.enqueue(project, 'create');
      return project;
    },
    list: (): Promise<readonly Project[]> => this.d.projects.listProjects(),
    update: async (id: EntityId, patch: ProjectPatch): Promise<Project> => {
      const project = await this.d.projects.updateProject(id, patch);
      await this.enqueue(project, 'update');
      return project;
    },
    setStatus: async (id: EntityId, status: ProjectStatus): Promise<Project> => {
      const project = await this.d.projects.setStatus(id, status);
      await this.enqueue(project, 'update');
      return project;
    },
  };

  private async expandEventsBetween(startIso: string, endIso: string): Promise<EventOccurrence[]> {
    const events = await this.d.events.listEvents();
    const out: EventOccurrence[] = [];
    for (const e of events) out.push(...expandOccurrences(e, startIso, endIso));
    return out.sort((a, b) => a.startsAt.localeCompare(b.startsAt));
  }

  readonly events = {
    create: async (input: EventInput): Promise<CalendarEvent> => {
      const event = await this.d.events.createEvent({
        ownerId: this.d.owner.ownerId,
        title: input.title,
        startsAt: input.startsAt,
        durationMinutes: input.durationMinutes ?? 60,
        location: input.location ?? '',
        recurrence: input.recurrence ?? { freq: 'none', interval: 1, until: null },
        privacyClass: input.privacyClass ?? 'sensitive_private',
      });
      await this.enqueue(event, 'create');
      return event;
    },
    list: (): Promise<readonly CalendarEvent[]> => this.d.events.listEvents(),
    update: async (id: EntityId, patch: EventPatch): Promise<CalendarEvent> => {
      const event = await this.d.events.updateEvent(id, patch);
      await this.enqueue(event, 'update');
      return event;
    },
    setStatus: async (id: EntityId, status: EventStatus): Promise<CalendarEvent> => {
      const event = await this.d.events.setStatus(id, status);
      await this.enqueue(event, 'update');
      return event;
    },
    occurrencesOn: (dateIso: string): Promise<readonly EventOccurrence[]> => {
      const { startIso, endIso } = dayWindow(dateIso);
      return this.expandEventsBetween(startIso, endIso);
    },
    occurrencesBetween: (startIso: string, endIso: string): Promise<readonly EventOccurrence[]> =>
      this.expandEventsBetween(startIso, endIso),
  };

  readonly finance = {
    createAccount: async (input: AccountInput): Promise<Account> => {
      const account = await this.d.finance.createAccount({
        ownerId: this.d.owner.ownerId,
        name: input.name,
        accountType: input.accountType ?? 'bank',
        currency: input.currency ?? 'USD',
        openingBalanceMinor: input.openingBalanceMinor ?? 0,
        privacyClass: input.privacyClass ?? 'finance_vault',
      });
      await this.enqueue(account, 'create');
      return account;
    },
    listAccounts: (): Promise<readonly Account[]> => this.d.finance.listAccounts(),
    recordTransaction: async (input: TransactionInput): Promise<Transaction> => {
      const account = await this.d.finance.getAccount(input.accountId);
      if (!account) throw new RecordNotFoundError('Account', String(input.accountId));
      const txn = await this.d.finance.createTransaction({
        ownerId: this.d.owner.ownerId,
        accountId: input.accountId,
        txnKind: input.txnKind,
        amountMinor: input.amountMinor,
        category: input.category ?? 'uncategorized',
        occurredOn: input.occurredOn ?? this.d.clock.nowIso(),
        note: input.note ?? '',
        occurredAt: input.occurredAt ?? null,
        counterparty: input.counterparty ?? null,
        externalReference: input.externalReference ?? null,
        rail: input.rail ?? null,
        balanceAfterMinor: input.balanceAfterMinor ?? null,
        importFingerprint: input.importFingerprint ?? null,
        importBatchId: input.importBatchId ?? null,
        privacyClass: input.privacyClass ?? account.privacyClass,
      });
      await this.enqueue(txn, 'create');
      return txn;
    },
    listTransactions: (accountId?: EntityId): Promise<readonly Transaction[]> =>
      this.d.finance.listTransactions(accountId),
    listImportBatches: (): Promise<readonly ImportBatch[]> => this.d.finance.listImportBatches(),
    /**
     * Idempotently ingest a normalized statement into an account. Rows whose
     * import fingerprint already exists are skipped, so re-importing overlapping
     * statements never duplicates. Records an audit ImportBatch.
     */
    importStatement: async (args: {
      accountId: EntityId;
      sourceLabel: string;
      sourceFiles: readonly string[];
      rows: readonly ImportRow[];
    }): Promise<{ created: number; skipped: number; batch: ImportBatch }> => {
      const account = await this.d.finance.getAccount(args.accountId);
      if (!account) throw new RecordNotFoundError('Account', String(args.accountId));
      const existing = await this.d.finance.listTransactions(args.accountId);
      const seen = new Set(existing.map((t) => t.importFingerprint).filter((f): f is string => Boolean(f)));

      const batch = await this.d.finance.createImportBatch({
        ownerId: this.d.owner.ownerId,
        accountId: args.accountId,
        sourceLabel: args.sourceLabel,
        sourceFiles: args.sourceFiles,
        rowsSeen: args.rows.length,
        rowsCreated: 0,
        rowsSkipped: 0,
        importedOn: this.d.clock.nowIso(),
        note: '',
        privacyClass: account.privacyClass,
      });
      await this.enqueue(batch, 'create');

      let created = 0;
      let skipped = 0;
      for (const row of args.rows) {
        const fingerprint = buildImportFingerprint(args.accountId, row);
        if (seen.has(fingerprint)) {
          skipped += 1;
          continue;
        }
        seen.add(fingerprint);
        const txn = await this.d.finance.createTransaction({
          ownerId: this.d.owner.ownerId,
          accountId: args.accountId,
          txnKind: row.txnKind,
          amountMinor: row.amountMinor,
          category: row.category ?? inferTransactionCategory(row.narration, row.txnKind),
          occurredOn: row.occurredOn,
          note: row.narration,
          occurredAt: row.occurredAt ?? null,
          counterparty: row.counterparty ?? null,
          externalReference: row.externalReference ?? null,
          rail: row.rail ?? null,
          balanceAfterMinor: row.balanceAfterMinor ?? null,
          importFingerprint: fingerprint,
          importBatchId: batch.id,
          privacyClass: account.privacyClass,
        });
        await this.enqueue(txn, 'create');
        created += 1;
      }
      return { created, skipped, batch };
    },
    balanceOf: async (accountId: EntityId): Promise<number> => {
      const account = await this.d.finance.getAccount(accountId);
      if (!account) throw new RecordNotFoundError('Account', String(accountId));
      const txns = await this.d.finance.listTransactions(accountId);
      return accountBalanceMinor(account, txns);
    },
    netWorth: async (): Promise<number> => {
      const [accounts, txns] = await Promise.all([
        this.d.finance.listAccounts(),
        this.d.finance.listTransactions(),
      ]);
      return netWorthMinor(accounts, txns);
    },
    summary: async (fromIso: string, toIso: string): Promise<FinanceSummary> => {
      const txns = await this.d.finance.listTransactions();
      return summarizeTransactions(txns, fromIso, toIso);
    },
  };

  readonly notes = {
    create: async (input: NoteInput): Promise<Note> => {
      const note = await this.d.notes.createNote({
        ownerId: this.d.owner.ownerId,
        title: input.title,
        blocks: input.blocks ?? [],
        tags: input.tags ?? [],
        privacyClass: input.privacyClass ?? 'sensitive_private',
      });
      await this.enqueue(note, 'create');
      return note;
    },
    list: (): Promise<readonly Note[]> => this.d.notes.listNotes(),
    get: (id: EntityId): Promise<Note | undefined> => this.d.notes.getNote(id),
    update: async (id: EntityId, patch: NotePatch): Promise<Note> => {
      const note = await this.d.notes.updateNote(id, patch);
      await this.enqueue(note, 'update');
      return note;
    },
    archive: async (id: EntityId): Promise<Note> => {
      const note = await this.d.notes.archiveNote(id);
      await this.enqueue(note, 'update');
      return note;
    },
    graph: async (): Promise<KnowledgeGraph> => {
      const notes = await this.d.notes.listNotes();
      return buildKnowledgeGraph(notes);
    },
  };

  readonly direction = {
    createGoal: async (input: GoalInput): Promise<Goal> => {
      const goal = await this.d.direction.createGoal({
        ownerId: this.d.owner.ownerId,
        title: input.title,
        pillar: input.pillar ?? 'mission',
        horizon: input.horizon ?? 'year',
        why: input.why ?? '',
        privacyClass: input.privacyClass ?? 'sensitive_private',
      });
      await this.enqueue(goal, 'create');
      return goal;
    },
    listGoals: (): Promise<readonly Goal[]> => this.d.direction.listGoals(),
    updateGoal: async (id: EntityId, patch: GoalPatch): Promise<Goal> => {
      const goal = await this.d.direction.updateGoal(id, patch);
      await this.enqueue(goal, 'update');
      return goal;
    },
    setGoalStatus: async (id: EntityId, status: GoalStatus): Promise<Goal> => {
      const goal = await this.d.direction.setGoalStatus(id, status);
      await this.enqueue(goal, 'update');
      return goal;
    },
    createMilestone: async (input: MilestoneInput): Promise<Milestone> => {
      const milestone = await this.d.direction.createMilestone({
        ownerId: this.d.owner.ownerId,
        title: input.title,
        contributesToGoalIds: input.contributesToGoalIds ?? [],
        privacyClass: input.privacyClass ?? 'sensitive_private',
      });
      await this.enqueue(milestone, 'create');
      return milestone;
    },
    listMilestones: (): Promise<readonly Milestone[]> => this.d.direction.listMilestones(),
    setMilestoneStatus: async (id: EntityId, status: MilestoneStatus): Promise<Milestone> => {
      const milestone = await this.d.direction.setMilestoneStatus(id, status);
      await this.enqueue(milestone, 'update');
      return milestone;
    },
    linkMilestoneToGoal: async (milestoneId: EntityId, goalId: EntityId): Promise<Milestone> => {
      const milestone = await this.d.direction.getMilestone(milestoneId);
      if (!milestone) throw new RecordNotFoundError('Milestone', String(milestoneId));
      const has = milestone.contributesToGoalIds.some((id) => String(id) === String(goalId));
      const next = has
        ? milestone.contributesToGoalIds.filter((id) => String(id) !== String(goalId))
        : [...milestone.contributesToGoalIds, goalId];
      const updated = await this.d.direction.setMilestoneGoals(milestoneId, next);
      await this.enqueue(updated, 'update');
      return updated;
    },
    progressOf: async (goalId: EntityId): Promise<GoalProgress> => {
      const milestones = await this.d.direction.listMilestones();
      return goalProgress(goalId, milestones);
    },
    addItem: async (input: DirectionItemInput): Promise<DirectionItem> => {
      const item = await this.d.direction.createItem({
        ownerId: this.d.owner.ownerId,
        lens: input.lens,
        itemKind: input.itemKind ?? defaultItemKind(input.lens),
        title: input.title,
        detail: input.detail ?? '',
        posture: input.posture ?? null,
        privacyClass: input.privacyClass ?? 'sensitive_private',
      });
      await this.enqueue(item, 'create');
      return item;
    },
    listItems: (lens: DirectionLens): Promise<readonly DirectionItem[]> => this.d.direction.listItems(lens),
    setItemPosture: async (id: EntityId, posture: ControlCategory | null): Promise<DirectionItem> => {
      const item = await this.d.direction.setItemPosture(id, posture);
      await this.enqueue(item, 'update');
      return item;
    },
    archiveItem: async (id: EntityId): Promise<DirectionItem> => {
      const item = await this.d.direction.archiveItem(id);
      await this.enqueue(item, 'update');
      return item;
    },
  };

  readonly health = {
    log: async (input: HealthLogInput): Promise<HealthLog> => {
      const log = await this.d.health.createLog({
        ownerId: this.d.owner.ownerId,
        occurredOn: input.occurredOn ?? this.d.clock.nowIso(),
        sleepHours: Math.max(0, input.sleepHours ?? 0),
        energy: clampEnergy(input.energy ?? 3),
        movementMinutes: Math.max(0, input.movementMinutes ?? 0),
        note: input.note ?? '',
        privacyClass: input.privacyClass ?? 'sensitive_private',
      });
      await this.enqueue(log, 'create');
      return log;
    },
    list: (): Promise<readonly HealthLog[]> => this.d.health.listLogs(),
    summary: async (fromIso: string, toIso: string): Promise<HealthSummary> => {
      const logs = await this.d.health.listLogs();
      return summarizeHealth(logs, fromIso, toIso);
    },
  };

  readonly academics = {
    getProfile: (): Promise<Profile | undefined> => this.d.academics.getProfile(),
    saveProfile: async (input: ProfileInput): Promise<Profile> => {
      const profile = await this.d.academics.upsertProfile({
        ownerId: this.d.owner.ownerId,
        displayName: input.displayName,
        headline: input.headline ?? '',
        location: input.location ?? '',
        education: input.education ?? [],
        links: input.links ?? [],
        privacyClass: input.privacyClass ?? 'standard_private',
      });
      await this.enqueue(profile, 'create');
      return profile;
    },
    createAdmission: async (input: AdmissionInput): Promise<Admission> => {
      const admission = await this.d.academics.createAdmission({
        ownerId: this.d.owner.ownerId,
        institution: input.institution,
        programme: input.programme,
        offerStatus: input.offerStatus ?? 'researching',
        conditions: input.conditions ?? [],
        currency: input.currency ?? 'GBP',
        tuitionMinor: input.tuitionMinor ?? 0,
        depositMinor: input.depositMinor ?? 0,
        livingEstimateMinor: input.livingEstimateMinor ?? 0,
        depositDueDate: input.depositDueDate ?? null,
        decisionDate: input.decisionDate ?? null,
        intakeTerm: input.intakeTerm ?? '',
        note: input.note ?? '',
        privacyClass: input.privacyClass ?? 'sensitive_private',
      });
      await this.enqueue(admission, 'create');
      return admission;
    },
    listAdmissions: (): Promise<readonly Admission[]> => this.d.academics.listAdmissions(),
    setOfferStatus: async (id: EntityId, offerStatus: OfferStatus): Promise<Admission> => {
      const a = await this.d.academics.setOfferStatus(id, offerStatus);
      await this.enqueue(a, 'update');
      return a;
    },
    setAdmissionStatus: async (id: EntityId, status: AdmissionStatus): Promise<Admission> => {
      const a = await this.d.academics.setAdmissionStatus(id, status);
      await this.enqueue(a, 'update');
      return a;
    },
    addFunding: async (input: FundingSourceInput): Promise<FundingSource> => {
      const f = await this.d.academics.createFunding({
        ownerId: this.d.owner.ownerId,
        name: input.name,
        sourceKind: input.sourceKind ?? 'scholarship',
        status: input.status ?? 'planned',
        currency: input.currency ?? 'GBP',
        amountMinor: input.amountMinor ?? 0,
        fullyFunded: input.fullyFunded ?? false,
        opensOn: input.opensOn ?? null,
        closesOn: input.closesOn ?? null,
        note: input.note ?? '',
        nextAction: input.nextAction ?? '',
        nextActionDate: input.nextActionDate ?? null,
        privacyClass: input.privacyClass ?? 'sensitive_private',
      });
      await this.enqueue(f, 'create');
      return f;
    },
    listFunding: (): Promise<readonly FundingSource[]> => this.d.academics.listFunding(),
    setFundingStatus: async (id: EntityId, status: FundingStatus): Promise<FundingSource> => {
      const f = await this.d.academics.setFundingStatus(id, status);
      await this.enqueue(f, 'update');
      return f;
    },
    setFundingNextAction: async (id: EntityId, nextAction: string, nextActionDate: string | null): Promise<FundingSource> => {
      const f = await this.d.academics.setFundingNextAction(id, nextAction, nextActionDate);
      await this.enqueue(f, 'update');
      return f;
    },
    logTouchpoint: async (
      id: EntityId,
      touchpoint: { on: string; channel: TouchpointChannel; note: string },
    ): Promise<FundingSource> => {
      const f = await this.d.academics.logTouchpoint(id, touchpoint);
      await this.enqueue(f, 'update');
      return f;
    },
    importEducationProgram: async (input: EducationProgramInput): Promise<EducationProgram> => {
      const program = await this.d.academics.upsertEducationProgram({
        ownerId: this.d.owner.ownerId,
        program: input,
        privacyClass: input.privacyClass ?? 'sensitive_private',
      });
      await this.enqueue(program, 'update');
      return program;
    },
    listEducationPrograms: (): Promise<readonly EducationProgram[]> => this.d.academics.listEducationPrograms(),
    importResumeProfile: async (input: ResumeProfileInput): Promise<ResumeProfile> => {
      const profile = await this.d.academics.upsertResumeProfile({
        ownerId: this.d.owner.ownerId,
        profile: input,
        privacyClass: input.privacyClass ?? 'sensitive_private',
      });
      await this.enqueue(profile, 'update');
      return profile;
    },
    getResumeProfile: (): Promise<ResumeProfile | undefined> => this.d.academics.getResumeProfile(),
  };

  readonly assistant = {
    brief: async (dateIso: string): Promise<Brief> => {
      const date = dateIso.slice(0, 10);
      const nowIso = this.d.clock.nowIso();

      const [plan, habits, targets, projects, problems, reviewsToday] = await Promise.all([
        this.today.buildTodayPlan(date),
        this.d.routines.listHabits(),
        this.d.targets.listTargets(),
        this.d.projects.listProjects(),
        this.d.problems.listProblems(),
        this.d.reviews.listForDate(date),
      ]);

      // Habits scheduled today and not yet checked in.
      const habitsDue: string[] = [];
      for (const habit of habits) {
        if (habit.status !== 'active') continue;
        const checkins = await this.d.routines.listCheckins(habit.id);
        const summary = computeHabitStreak(habit, checkins, nowIso);
        if (summary.scheduledToday && !summary.checkedInToday) habitsDue.push(habit.title);
      }

      // Targets whose current window needs attention.
      const targetsAtRisk: string[] = [];
      for (const target of targets) {
        const evidence = await this.d.targets.listEvidence(target.id);
        const summary = computeConsistency(target, evidence, nowIso);
        if (summary.state === 'needs_attention') targetsAtRisk.push(target.title);
      }

      const stalledProjects = projects.filter((p) => isProjectStalled(p)).map((p) => p.title);
      const openProblems = problems.filter((p) => isProblemOpen(p.status)).length;
      const unreviewedCaptures = await this.d.capture.listUnreviewed();

      const firstAgenda = plan.agenda[0];

      return composeBrief({
        dateIso: date,
        agendaCount: plan.agenda.length,
        nextAgendaTitle: firstAgenda?.title ?? null,
        nextAgendaAt: firstAgenda?.at ?? null,
        unreviewedCaptures: unreviewedCaptures.length,
        habitsDue,
        targetsAtRisk,
        stalledProjects,
        openProblems,
        reviewedToday: reviewsToday.length > 0,
      });
    },
  };

  readonly study = {
    createSubject: async (input: StudySubjectInput): Promise<StudySubject> => {
      const subject = await this.d.study.createSubject({
        ownerId: this.d.owner.ownerId,
        title: input.title,
        masteryState: input.masteryState ?? 'aware',
        privacyClass: input.privacyClass ?? 'sensitive_private',
      });
      await this.enqueue(subject, 'create');
      return subject;
    },
    listSubjects: (): Promise<readonly StudySubject[]> => this.d.study.listSubjects(),
    setMastery: async (id: EntityId, state: MasteryState): Promise<StudySubject> => {
      const subject = await this.d.study.setMastery(id, state);
      await this.enqueue(subject, 'update');
      return subject;
    },
    setSubjectStatus: async (id: EntityId, status: SubjectStatus): Promise<StudySubject> => {
      const subject = await this.d.study.setSubjectStatus(id, status);
      await this.enqueue(subject, 'update');
      return subject;
    },
    logSession: async (input: StudySessionInput): Promise<StudySession> => {
      const subject = await this.d.study.getSubject(input.subjectId);
      if (!subject) throw new RecordNotFoundError('StudySubject', String(input.subjectId));
      const session = await this.d.study.createSession({
        ownerId: this.d.owner.ownerId,
        subjectId: input.subjectId,
        minutes: input.minutes,
        occurredOn: input.occurredOn ?? this.d.clock.nowIso(),
        note: input.note ?? '',
        privacyClass: input.privacyClass ?? subject.privacyClass,
      });
      await this.enqueue(session, 'create');
      return session;
    },
    listSessions: (subjectId?: EntityId): Promise<readonly StudySession[]> => this.d.study.listSessions(subjectId),
    summary: async (fromIso: string, toIso: string): Promise<StudySummary> => {
      const sessions = await this.d.study.listSessions();
      return summarizeStudy(sessions, fromIso, toIso);
    },
  };

  readonly career = {
    create: async (input: OpportunityInput): Promise<Opportunity> => {
      const opp = await this.d.career.createOpportunity({
        ownerId: this.d.owner.ownerId,
        title: input.title,
        organization: input.organization ?? '',
        url: input.url ?? '',
        note: input.note ?? '',
        privacyClass: input.privacyClass ?? 'sensitive_private',
      });
      await this.enqueue(opp, 'create');
      return opp;
    },
    list: (): Promise<readonly Opportunity[]> => this.d.career.listOpportunities(),
    update: async (id: EntityId, patch: OpportunityPatch): Promise<Opportunity> => {
      const opp = await this.d.career.updateOpportunity(id, patch);
      await this.enqueue(opp, 'update');
      return opp;
    },
    setStage: async (id: EntityId, stage: OpportunityStage): Promise<Opportunity> => {
      const opp = await this.d.career.setStage(id, stage);
      await this.enqueue(opp, 'update');
      return opp;
    },
  };

  readonly journal = {
    add: async (input: JournalEntryInput): Promise<JournalEntry> => {
      const entry = await this.d.journal.createEntry({
        ownerId: this.d.owner.ownerId,
        stream: input.stream,
        entryKind: input.entryKind ?? (input.stream === 'spiritual' ? 'gratitude' : 'journal'),
        title: input.title ?? '',
        body: input.body,
        occurredOn: input.occurredOn ?? this.d.clock.nowIso(),
        privacyClass: input.privacyClass ?? defaultJournalPrivacy(input.stream),
      });
      await this.enqueue(entry, 'create');
      return entry;
    },
    list: (stream: JournalStream): Promise<readonly JournalEntry[]> => this.d.journal.listEntries(stream),
    summary: async (stream: JournalStream, fromIso: string, toIso: string): Promise<JournalSummary> => {
      const entries = await this.d.journal.listEntries(stream);
      return summarizeJournal(entries, stream, fromIso, toIso);
    },
  };

  readonly content = {
    create: async (input: ContentItemInput): Promise<ContentItem> => {
      const item = await this.d.content.createItem({
        ownerId: this.d.owner.ownerId,
        title: input.title,
        platform: input.platform ?? '',
        note: input.note ?? '',
        url: input.url ?? '',
        privacyClass: input.privacyClass ?? 'sensitive_private',
      });
      await this.enqueue(item, 'create');
      return item;
    },
    list: (): Promise<readonly ContentItem[]> => this.d.content.listItems(),
    update: async (id: EntityId, patch: ContentItemPatch): Promise<ContentItem> => {
      const item = await this.d.content.updateItem(id, patch);
      await this.enqueue(item, 'update');
      return item;
    },
    setStage: async (id: EntityId, stage: ContentStage): Promise<ContentItem> => {
      const item = await this.d.content.setStage(id, stage);
      await this.enqueue(item, 'update');
      return item;
    },
  };

  readonly business = {
    create: async (input: VentureInput): Promise<Venture> => {
      const venture = await this.d.business.createVenture({
        ownerId: this.d.owner.ownerId,
        title: input.title,
        sector: input.sector ?? '',
        monthlyRevenueMinor: 0,
        note: input.note ?? '',
        privacyClass: input.privacyClass ?? 'sensitive_private',
      });
      await this.enqueue(venture, 'create');
      return venture;
    },
    list: (): Promise<readonly Venture[]> => this.d.business.listVentures(),
    update: async (id: EntityId, patch: VenturePatch): Promise<Venture> => {
      const venture = await this.d.business.updateVenture(id, patch);
      await this.enqueue(venture, 'update');
      return venture;
    },
    setStatus: async (id: EntityId, status: VentureStatus): Promise<Venture> => {
      const venture = await this.d.business.setStatus(id, status);
      await this.enqueue(venture, 'update');
      return venture;
    },
  };

  readonly environment = {
    create: async (input: InventoryItemInput): Promise<InventoryItem> => {
      const item = await this.d.environment.createItem({
        ownerId: this.d.owner.ownerId,
        name: input.name,
        category: input.category ?? 'device',
        location: input.location ?? '',
        note: input.note ?? '',
        privacyClass: input.privacyClass ?? 'standard_private',
      });
      await this.enqueue(item, 'create');
      return item;
    },
    list: (): Promise<readonly InventoryItem[]> => this.d.environment.listItems(),
    update: async (id: EntityId, patch: InventoryItemPatch): Promise<InventoryItem> => {
      const item = await this.d.environment.updateItem(id, patch);
      await this.enqueue(item, 'update');
      return item;
    },
    setStatus: async (id: EntityId, status: ItemStatus): Promise<InventoryItem> => {
      const item = await this.d.environment.setStatus(id, status);
      await this.enqueue(item, 'update');
      return item;
    },
  };

  readonly focus = {
    logSession: async (input: FocusSessionInput): Promise<FocusSession> => {
      const session = await this.d.focus.createSession({
        ownerId: this.d.owner.ownerId,
        minutes: input.minutes,
        occurredOn: input.occurredOn ?? this.d.clock.nowIso(),
        note: input.note ?? '',
        linkedId: input.linkedId ?? null,
        privacyClass: input.privacyClass ?? 'sensitive_private',
      });
      await this.enqueue(session, 'create');
      return session;
    },
    list: (): Promise<readonly FocusSession[]> => this.d.focus.listSessions(),
    summary: async (fromIso: string, toIso: string): Promise<FocusSummary> => {
      const sessions = await this.d.focus.listSessions();
      return summarizeFocus(sessions, fromIso, toIso);
    },
  };

  readonly security = {
    status: async (): Promise<LockStatus> => {
      const config = (await this.d.security.getConfig()) ?? DEFAULT_SECURITY_CONFIG;
      return lockStatus(config, this.unlocked);
    },
    getConfig: async (): Promise<SecurityConfig> => (await this.d.security.getConfig()) ?? DEFAULT_SECURITY_CONFIG,
    setPassphrase: async (passphrase: string): Promise<void> => {
      const current = (await this.d.security.getConfig()) ?? DEFAULT_SECURITY_CONFIG;
      const verifier = await createVerifier(passphrase);
      await this.d.security.putConfig({ ...current, verifier });
      this.unlocked = true;
      await this.d.security.appendEvent({ ownerId: this.d.owner.ownerId, eventType: 'passphrase_set', at: this.d.clock.nowIso(), detail: '' });
    },
    clearPassphrase: async (passphrase: string): Promise<boolean> => {
      const current = (await this.d.security.getConfig()) ?? DEFAULT_SECURITY_CONFIG;
      if (!current.verifier) return false;
      const ok = await verifyPassphrase(passphrase, current.verifier);
      if (!ok) {
        await this.d.security.appendEvent({ ownerId: this.d.owner.ownerId, eventType: 'failed_unlock', at: this.d.clock.nowIso(), detail: 'clear' });
        return false;
      }
      await this.d.security.putConfig({ ...current, verifier: null });
      this.unlocked = true;
      await this.d.security.appendEvent({ ownerId: this.d.owner.ownerId, eventType: 'passphrase_cleared', at: this.d.clock.nowIso(), detail: '' });
      return true;
    },
    unlock: async (passphrase: string): Promise<boolean> => {
      const config = (await this.d.security.getConfig()) ?? DEFAULT_SECURITY_CONFIG;
      if (!config.verifier) {
        this.unlocked = true;
        return true;
      }
      const ok = await verifyPassphrase(passphrase, config.verifier);
      this.unlocked = ok;
      await this.d.security.appendEvent({
        ownerId: this.d.owner.ownerId,
        eventType: ok ? 'unlock' : 'failed_unlock',
        at: this.d.clock.nowIso(),
        detail: '',
      });
      return ok;
    },
    lock: async (reason: 'manual' | 'auto' = 'manual'): Promise<void> => {
      this.unlocked = false;
      await this.d.security.appendEvent({
        ownerId: this.d.owner.ownerId,
        eventType: reason === 'auto' ? 'auto_lock' : 'lock',
        at: this.d.clock.nowIso(),
        detail: '',
      });
    },
    setAutoLockMinutes: async (minutes: number): Promise<void> => {
      const current = (await this.d.security.getConfig()) ?? DEFAULT_SECURITY_CONFIG;
      await this.d.security.putConfig({ ...current, autoLockMinutes: Math.max(0, Math.trunc(minutes)) });
    },
    setRequireStepUpForVault: async (required: boolean): Promise<void> => {
      const current = (await this.d.security.getConfig()) ?? DEFAULT_SECURITY_CONFIG;
      await this.d.security.putConfig({ ...current, requireStepUpForVault: required });
    },
    listAudit: (limit: number): Promise<readonly SecurityEvent[]> => this.d.security.listEvents(limit),
  };

  readonly today = {
    buildTodayPlan: async (date: string): Promise<TodayPlan> => {
      const { startIso, endIso } = dayWindow(date);
      const [now, next, scheduled, reminders, unreviewed, reviewsToday, occurrences] = await Promise.all([
        this.d.tasks.listByLane('now'),
        this.d.tasks.listByLane('next'),
        this.d.tasks.listScheduledOn(date),
        this.d.reminders.listFiringOn(date),
        this.d.capture.listUnreviewed(),
        this.d.reviews.listForDate(date),
        this.expandEventsBetween(startIso, endIso),
      ]);

      const agenda: AgendaItem[] = [
        ...scheduled
          .filter((t): t is Task & { scheduledFor: string } => t.scheduledFor !== null)
          .map((t) => ({ id: String(t.id), title: t.title, at: t.scheduledFor, kind: 'scheduled_task' as const })),
        ...occurrences.map((o) => ({
          id: `${String(o.eventId)}@${o.startsAt}`,
          title: o.title,
          at: o.startsAt,
          kind: 'event' as const,
        })),
        ...reminders.map((r) => ({
          id: String(r.id),
          title: r.label,
          at: r.fireAt,
          kind: 'reminder' as const,
        })),
      ].sort((a, b) => a.at.localeCompare(b.at));

      const prompts: ReviewPrompt[] = [];
      if (unreviewed.length > 0) {
        prompts.push({ id: 'triage-captures', label: `Triage ${unreviewed.length} captured item(s)` });
      }
      if (reviewsToday.length === 0) {
        prompts.push({ id: 'daily-review', label: 'Run your daily review' });
      }

      return {
        date,
        now,
        next,
        agenda,
        reviewPrompts: prompts.slice(0, MAX_REVIEW_PROMPTS),
      };
    },
  };
}
